/**
 * Service de chargement du fichier de référence œuvres/mouvements.
 * Charge dans l'ordre : works_enriched.json, sinon works.json, sinon works_from_wikidata.json.
 * Indexe par compositeur.
 */

let loadPromise = null
let slugToData = null

/**
 * Génère un slug de compositeur (ex. "Mozart" -> "mozart", "J.S. Bach" -> "js-bach").
 */
export function composerToSlug(composerKey) {
  if (!composerKey || typeof composerKey !== 'string') return ''
  return composerKey
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/\u0300-\u036f/g, '')
    .replace(/[\s.'']+/g, '-')
    .replace(/[^a-z0-9-]/g, '')
    .replace(/-+/g, '-')
    .replace(/^-|-$/g, '')
}

function buildIndex(payload) {
  const index = new Map()
  if (!payload || !Array.isArray(payload.composers)) return index
  for (const c of payload.composers) {
    const name = c.composerName
    if (!name) continue
    const slug = composerToSlug(name)
    if (slug) index.set(slug, { composerId: c.composerId, composerName: name, works: c.works || [] })
  }
  return index
}

async function ensureLoaded() {
  if (slugToData) return
  if (!loadPromise) {
    loadPromise = (async () => {
      try {
        const base = typeof import.meta.env?.BASE_URL === 'string' ? import.meta.env.BASE_URL.replace(/\/$/, '') : ''
        let res = await fetch(`${base}/data/works_enriched.json`)
        if (!res.ok) res = await fetch(`${base}/data/works.json`)
        if (!res.ok) res = await fetch(`${base}/data/works_from_wikidata.json`)
        if (!res.ok) {
          console.warn('works_enriched / works.json / works_from_wikidata non chargé (', res.status, ')')
          slugToData = new Map()
          return
        }
        const data = await res.json()
        if (!data || !Array.isArray(data.composers) || data.composers.length === 0) {
          console.warn('[worksReference] Fichier de référence invalide ou vide : attendu { composers: [...] }.')
          slugToData = new Map()
        } else {
          slugToData = buildIndex(data)
        }
      } catch (e) {
        console.warn('Fichier de référence œuvres non chargé', e)
        slugToData = new Map()
      }
    })()
  }
  await loadPromise
}

/**
 * Retourne la liste des compositeurs connus (works.json) pour le matching "compositeur d'abord".
 * À appeler après ensureLoaded() (ou elle sera appelée ici).
 * @returns {Promise<Array<{ name: string, slug: string }>>}
 */
export async function getKnownComposerNames() {
  await ensureLoaded()
  if (!slugToData) return []
  const list = []
  for (const [slug, data] of slugToData) {
    if (data.composerName) list.push({ name: data.composerName, slug })
  }
  return list
}

/**
 * Charge les œuvres d'un compositeur. Retourne null si non trouvé ou erreur.
 * @param {string} composerKey - Nom du compositeur (ex. "Mozart", "J.S. Bach")
 * @returns {Promise<{ composerId?: string, composerName: string, works: Array<{ workTitle: string, movements: string[], workType?: string }> } | null>}
 */
export async function getWorksByComposer(composerKey) {
  const slug = composerToSlug(composerKey)
  if (!slug) return null
  await ensureLoaded()
  return slugToData.get(slug) || null
}

/**
 * Filtre les œuvres dont le titre (ou alias) contient la requête (insensible à la casse).
 */
export async function findWorksByTitle(composerKey, query) {
  const data = await getWorksByComposer(composerKey)
  if (!data || !data.works || !query || !query.trim()) return []
  const q = normalizeForMatch(query.trim())
  return data.works.filter((w) => {
    const title = normalizeForMatch(w.workTitle)
    if (title.includes(q) || q.includes(title)) return true
    if (w.aliases && Array.isArray(w.aliases)) {
      return w.aliases.some((a) => normalizeForMatch(a).includes(q) || q.includes(normalizeForMatch(a)))
    }
    return false
  })
}

// Suffixes de type genre (OpenOpus, etc.) à retirer pour matcher titre vidéo et référence
const TRAILING_GENRE_SUFFIX = /\s*-\s*(ballet|opera|orchestral|stage|vocal|chamber|keyboard|instrumental|electronic|film|other)\s*$/i

// " for keyboard", " for piano", etc. : à retirer pour que "Partita for keyboard No. 2" matche "Partita no. 2 in C minor"
const FOR_INSTRUMENT_SUFFIX = /\s+for\s+(keyboard|piano|solo\s+violin|violin|harpsichord|organ|cello|flute|orchestra|strings|wind\s+instruments?)\b/gi

/**
 * Table canonique instrument → termes multilingues (EN, FR, DE, IT, ES, etc.).
 * Utilisée pour extraire les indices d'instrument du titre parsé et des titres de référence (tie-break).
 */
const INSTRUMENT_TERMS = {
  keyboard: [
    'piano', 'keyboard', 'clavier', 'klavier', 'clavecin', 'harpsichord', 'pianoforte', 'teclado', 'tastiera',
    'clavicembalo', 'cembalo', 'fortepiano'
  ],
  violin: [
    'violin', 'violon', 'violino', 'geige', 'violín', 'solo violin', 'violon solo', 'violino solo'
  ],
  cello: [
    'cello', 'violoncelle', 'violoncello', 'violoncelles', 'violoncelo', 'violoncellos'
  ],
  flute: [
    'flute', 'flûte', 'flöte', 'flauto', 'flauta'
  ],
  viola: [
    'viola', 'alto'
  ],
  organ: [
    'organ', 'orgue', 'orgel', 'organo', 'órgano'
  ],
  harpsichord: [
    'harpsichord', 'clavecin', 'clavicembalo', 'cembalo', 'clave'
  ],
  orchestra: [
    'orchestra', 'orchestre', 'orchestr'
  ],
  clarinet: [
    'clarinet', 'clarinette', 'klarinette', 'clarinetto'
  ],
  guitar: [
    'guitar', 'guitare', 'gitarre', 'chitarra', 'guitarra'
  ],
  voice: [
    'voice', 'voix', 'stimme', 'voce', 'voz', 'chant', 'singing'
  ]
}

/** Regex par instrument (termes normalisés en \b(term1|term2|...)\b) pour détection dans une chaîne normalisée. */
let INSTRUMENT_REGEXES = null
function getInstrumentRegexes() {
  if (INSTRUMENT_REGEXES) return INSTRUMENT_REGEXES
  const out = {}
  for (const [canonical, terms] of Object.entries(INSTRUMENT_TERMS)) {
    const normalizedTerms = [...new Set(terms.map((t) => normalizeForInstrumentSearch(t)))]
    const escaped = normalizedTerms
      .filter(Boolean)
      .map((t) => t.replace(/[.*+?^${}()|[\]\\]/g, '\\$&'))
    if (escaped.length) out[canonical] = new RegExp('\\b(' + escaped.join('|') + ')\\b', 'i')
  }
  INSTRUMENT_REGEXES = out
  return out
}

/** Normalisation légère pour la recherche d'instruments (lowercase, NFD, sans accents). */
function normalizeForInstrumentSearch(s) {
  if (!s || typeof s !== 'string') return ''
  return s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\u0300-\u036f/g, '')
    .replace(/\s+/g, ' ')
    .trim()
}

/**
 * Extrait les indices d'instrument canoniques depuis une chaîne (titre parsé ou workTitle).
 * @param {string} title - Chaîne brute
 * @returns {Set<string>} Clés canoniques (keyboard, violin, cello, ...)
 */
function getInstrumentHintsFromString(title) {
  const hints = new Set()
  if (!title || typeof title !== 'string') return hints
  const normalized = normalizeForInstrumentSearch(title)
  if (!normalized) return hints
  const regexes = getInstrumentRegexes()
  for (const [canonical, re] of Object.entries(regexes)) {
    if (re.test(normalized)) hints.add(canonical)
  }
  return hints
}

/**
 * Indices d'instrument pour une œuvre de référence (titre + workType).
 * workType "Keyboard" → ajoute keyboard (œuvres sans "keyboard" dans le titre).
 * @param {{ workTitle: string, workType?: string }} work
 * @returns {Set<string>}
 */
function getInstrumentHintsFromWork(work) {
  const hints = new Set()
  if (!work) return hints
  if (work.workTitle) {
    const fromTitle = getInstrumentHintsFromString(work.workTitle)
    fromTitle.forEach((h) => hints.add(h))
  }
  if (work.workType === 'Keyboard') hints.add('keyboard')
  return hints
}

function normalizeForMatch(s) {
  if (!s || typeof s !== 'string') return ''
  let out = s
    .toLowerCase()
    .normalize('NFD')
    .replace(/\u0300-\u036f/g, '')
    .replace(/\s+/g, ' ')
    .trim()
  // Retirer parenthèses (ex. "(with Score)", "(Kovacevich, Biret)") et guillemets (droits + typographiques " " ' ')
  out = out.replace(/\s*\([^)]*\)/g, ' ')
  out = out.replace(/\s*["'\u201C\u201D\u2018\u2019][^"'\u201C\u201D\u2018\u2019]*["'\u201C\u201D\u2018\u2019]/g, ' ')
  out = out.replace(/\s+/g, ' ').trim()
  // Retirer suffixe " - Ballet", " - Orchestral", etc. pour matcher titre vidéo et référence OpenOpus
  out = out.replace(TRAILING_GENRE_SUFFIX, '').replace(/\s+/g, ' ').trim()
  // Unifier ponctuation (virgule / tiret) pour "The Seasons, Op. 67" vs "The Seasons - op. 67 - Ballet"
  out = out.replace(/\s*[,\-–—]\s*/g, ' ').replace(/\s+/g, ' ').trim()
  // Retirer " for keyboard", " for piano", etc. pour que "Partita for keyboard No. 2" → "Partita No. 2"
  out = out.replace(FOR_INSTRUMENT_SUFFIX, '').replace(/\s+/g, ' ').trim()
  // Unifier "no. 2" / "n° 2" / "number 2" en "2" pour que "Partita no. 2" et "Partita 2" matchent
  out = out.replace(/\b(no\.?|n°|nr\.?|number)\s*(\d+)/gi, ' $2').replace(/\s+/g, ' ').trim()
  return out
}

function titlesMatch(parsed, reference) {
  const a = normalizeForMatch(parsed)
  const b = normalizeForMatch(reference)
  if (a === b) return true
  // Normaliser no./n°/#/number pour comparer "Symphony # 31" et "Symphony no. 31"
  const numPattern = /\b(no\.?|n°|number|nr|#)\s*(\d+)/gi
  const aNum = a.replace(numPattern, '$2').replace(/\s+/g, ' ')
  const bNum = b.replace(numPattern, '$2').replace(/\s+/g, ' ')
  if (aNum === bNum) return true
  if (a.includes(b) || b.includes(a)) return true
  const corePattern = /\b(symphony|symphonie|sinfonia|sinfonie)\s*(no\.?|n°|number|#)?\s*\d*/gi
  const aCore = a.replace(corePattern, 'symphony').replace(/\s+/g, ' ')
  const bCore = b.replace(corePattern, 'symphony').replace(/\s+/g, ' ')
  if (aCore === bCore) return true
  // Même symphonie par numéro + surnom (ex. "Symphonie n°6 Pastorale" vs "Symphony no. 6 ..." ou "8. Sinfonie" vs "Symphony no. 8")
  const symNumRe = /(?:symphony|symphonie|sinfonia|sinfonie)\s*(?:no\.?|n°|number|nr|#)?\s*(\d+)/i
  const symNumFirstRe = /(\d+)\s*\.?\s*(?:symphony|symphonie|sinfonia|sinfonie)/i  // format "8. Sinfonie"
  const numA = a.match(symNumRe)?.[1] ?? a.match(symNumFirstRe)?.[1]
  const numB = b.match(symNumRe)?.[1] ?? b.match(symNumFirstRe)?.[1]
  if (numA && numB && numA === numB) {
    const nickRe = /["'\u201C\u201D\u2018\u2019]([^"'\u201C\u201D\u2018\u2019]+)["'\u201C\u201D\u2018\u2019]/g
    const nickA = [...(a.matchAll(nickRe) || [])].map((m) => normalizeForMatch(m[1])).filter(Boolean)
    const nickB = [...(b.matchAll(nickRe) || [])].map((m) => normalizeForMatch(m[1])).filter(Boolean)
    const hasSameNick = nickA.length === 0 && nickB.length === 0 ||
      nickA.some((n) => nickB.some((m) => n === m || n.includes(m) || m.includes(n)))
    if (hasSameNick) return true
  }
  return false
}

const MATCH_SCORE_THRESHOLD = 0.6

// Types d'œuvre dont un chiffre juste après peut être un numéro d'œuvre (ex. "Symphony 5", "Sonata 14")
const WORK_TYPE_FOR_NUMBER = /\b(symphony|symphonie|sinfonia|sinfonie|sonata|concerto|trio|quartet|quatuor|quartett)\s*\.?\s*(\d+)(?:st|nd|rd|th|ème|eme|er|re)?\b/i

/**
 * Extrait les numéros de musique par catégorie (opus, no d'œuvre, catalogues).
 * - Mots-clés explicites : No., Op., BWV, K., D., Hob.
 * - Ordinaux : 5th, 2nd, 5ème, 1re → traités comme No.
 * - Chiffre orphelin : "Symphony 5" ou "Sonata 14" (type d'œuvre + chiffre) si aucun No explicite.
 * @param {string} s - Chaîne déjà normalisée (lowercase, sans accents)
 * @returns {{ opus?: string, no?: string, bwv?: string, k?: string, d?: string, hob?: string }}
 */
function getMusicNumbers(s) {
  if (!s || typeof s !== 'string') return {}
  const out = {}

  const opusMatch = s.match(/\bop(?:us)?\.?\s*(\d+)/i)
  if (opusMatch) out.opus = opusMatch[1]

  const noMatch = s.match(/\b(?:no\.?|n°|nr\.?|number)\s*(\d+)/i)
  if (noMatch) out.no = noMatch[1]

  // Ordinaux (5th, 2nd, 5ème, 1re) : considérés comme numéro d'œuvre si pas déjà de No
  if (!out.no) {
    const ordinalMatch = s.match(/\b(\d+)(?:st|nd|rd|th|ème|eme|er|re)\b/i)
    if (ordinalMatch) out.no = ordinalMatch[1]
  }

  // Chiffre après type d'œuvre ("Symphony 5", "Sonata 14") si toujours pas de No
  if (!out.no) {
    const workNumMatch = s.match(WORK_TYPE_FOR_NUMBER)
    if (workNumMatch) out.no = workNumMatch[2]
  }

  // Catalogues (espace optionnel : K550, BWV565)
  const bwvMatch = s.match(/\bbwv\s*(\d+)/i)
  if (bwvMatch) out.bwv = bwvMatch[1]
  const kMatch = s.match(/\bk(?:v)?\.?\s*(\d+)/i)
  if (kMatch) out.k = kMatch[1]
  const dMatch = s.match(/\bd\.\s*(\d+)/i)
  if (dMatch) out.d = dMatch[1]
  const hobMatch = s.match(/\bhob\.?\s*(\d+)/i)
  if (hobMatch) out.hob = hobMatch[1]

  return out
}

/**
 * Conflit dur : le MÊME type de numéro est présent dans les deux titres et les valeurs diffèrent.
 * Si un titre a Op. 67 et l'autre No. 5 (sans Opus), on ne rejette pas.
 * @param {string} s1 - Chaîne normalisée (parsed)
 * @param {string} s2 - Chaîne normalisée (reference)
 * @returns {boolean}
 */
function isHardConflict(s1, s2) {
  const p = getMusicNumbers(s1)
  const r = getMusicNumbers(s2)
  if (p.opus && r.opus && p.opus !== r.opus) return true
  if (p.no && r.no && p.no !== r.no) return true
  if (p.bwv && r.bwv && p.bwv !== r.bwv) return true
  if (p.k && r.k && p.k !== r.k) return true
  if (p.d && r.d && p.d !== r.d) return true
  if (p.hob && r.hob && p.hob !== r.hob) return true
  return false
}

/**
 * Calcule un score de similarité entre deux chaînes (titre parsé vs référence).
 * Règle brise-glace : on ne compare que les numéros de même catégorie (Opus, No., BWV, K., etc.) ;
 * si le même type est présent dans les deux et diffère, score 0. Les chiffres isolés (ex. mouvement) sont ignorés.
 * @param {string} parsed
 * @param {string} reference
 * @returns {number} score entre 0 et 1
 */
function calculateSimilarity(parsed, reference) {
  const s1 = normalizeForMatch(parsed)
  const s2 = normalizeForMatch(reference)
  if (!s1 || !s2) return 0

  // Brise-glace catégorisé : conflit seulement si même catégorie (Opus, No., BWV, …) et valeurs différentes
  if (isHardConflict(s1, s2)) return 0

  if (titlesMatch(parsed, reference)) return 1.0
  if (s1 === s2) return 1.0
  if (s1.includes(s2) || s2.includes(s1)) return 0.85

  const words1 = new Set(s1.split(/\s+/).filter((w) => w.length > 1))
  const words2 = new Set(s2.split(/\s+/).filter((w) => w.length > 1))
  if (words1.size === 0 || words2.size === 0) return 0
  const intersection = [...words1].filter((x) => words2.has(x))
  return intersection.length / Math.max(words1.size, words2.size)
}

/**
 * Trouve dans la base l'œuvre qui correspond au titre parsé (ex. titre vidéo).
 * Matching par score : seuil 0.6 ; retourne le meilleur match avec son score.
 * @returns {Promise<{ work: { workTitle: string, movements: string[] }, composerName: string, matchScore: number } | null>}
 */
/** Nombre d'éléments en commun entre deux Sets (pour tie-break par instrument). */
function instrumentOverlap(parsedHints, workHints) {
  if (!parsedHints.size) return 0
  let n = 0
  for (const h of parsedHints) {
    if (workHints.has(h)) n++
  }
  return n
}

export async function findMatchingWork(composerKey, parsedWorkTitle) {
  const data = await getWorksByComposer(composerKey)
  if (!data || !data.works || !parsedWorkTitle || !parsedWorkTitle.trim()) return null
  const composerName = data.composerName || composerKey
  const parsed = parsedWorkTitle.trim()

  const parsedInstrumentHints = getInstrumentHintsFromString(parsed)
  let bestMatch = null
  let highestScore = 0
  let bestOverlap = 0

  for (const work of data.works) {
    let score = calculateSimilarity(parsed, work.workTitle)
    if (work.aliases && Array.isArray(work.aliases)) {
      for (const alias of work.aliases) {
        const aliasScore = calculateSimilarity(parsed, alias)
        if (aliasScore > score) score = aliasScore
      }
    }
    if (score > highestScore) {
      highestScore = score
      bestMatch = work
      bestOverlap = parsedInstrumentHints.size
        ? instrumentOverlap(parsedInstrumentHints, getInstrumentHintsFromWork(work))
        : 0
    } else if (score === highestScore && bestMatch) {
      if (parsedInstrumentHints.size > 0) {
        const candidateOverlap = instrumentOverlap(parsedInstrumentHints, getInstrumentHintsFromWork(work))
        if (candidateOverlap > bestOverlap) {
          bestMatch = work
          bestOverlap = candidateOverlap
        }
      }
    }
  }

  if (highestScore > MATCH_SCORE_THRESHOLD && bestMatch) {
    return { work: bestMatch, composerName, matchScore: highestScore }
  }
  return null
}

const COMPOSER_ERA_MAP = {
  'Bach': 'Baroque', 'Vivaldi': 'Baroque', 'Handel': 'Baroque', 'Händel': 'Baroque',
  'Telemann': 'Baroque', 'Purcell': 'Baroque', 'Scarlatti': 'Baroque', 'Corelli': 'Baroque',
  'Couperin': 'Baroque', 'Rameau': 'Baroque', 'Lully': 'Baroque', 'Monteverdi': 'Baroque',
  'Byrd': 'Baroque', 'Bach, C.P.E.': 'Classique',
  'Mozart': 'Classique', 'Haydn': 'Classique', 'Beethoven': 'Classique',
  'Clementi': 'Classique', 'Gluck': 'Classique', 'Salieri': 'Classique',
  'Chopin': 'Romantique', 'Schumann': 'Romantique', 'Liszt': 'Romantique',
  'Brahms': 'Romantique', 'Wagner': 'Romantique', 'Tchaikovsky': 'Romantique',
  'Dvořák': 'Romantique', 'Mendelssohn': 'Romantique', 'Schubert': 'Romantique',
  'Verdi': 'Romantique', 'Grieg': 'Romantique', 'Strauss Jr': 'Romantique',
  'Sibelius': 'Romantique', 'Rachmaninoff': 'Romantique', 'Saint-Saëns': 'Romantique',
  'Fauré': 'Romantique', 'Franck': 'Romantique', 'Bruckner': 'Romantique',
  'Debussy': 'Moderne', 'Ravel': 'Moderne', 'Stravinsky': 'Moderne',
  'Schoenberg': 'Moderne', 'Bartók': 'Moderne', 'Prokofiev': 'Moderne',
  'Shostakovich': 'Moderne', 'Poulenc': 'Moderne', 'Messiaen': 'Moderne',
  'Satie': 'Moderne', 'Hindemith': 'Moderne',
}

/**
 * Retourne la liste des compositeurs avec leur nombre d'oeuvres, triée par count décroissant.
 * @returns {Promise<Array<{ name: string, slug: string, worksCount: number, era: string|null }>>}
 */
export async function getComposerStats() {
  await ensureLoaded()
  if (!slugToData) return []
  const list = []
  for (const [slug, data] of slugToData) {
    if (!data.composerName) continue
    list.push({
      name: data.composerName,
      slug,
      worksCount: data.works?.length || 0,
      era: COMPOSER_ERA_MAP[data.composerName] || null,
    })
  }
  list.sort((a, b) => b.worksCount - a.worksCount)
  return list
}

/**
 * Retourne les compositeurs groupés par époque.
 * @returns {Promise<Record<string, Array<{ name: string, slug: string, worksCount: number }>>>}
 */
export async function getComposersByEra() {
  const stats = await getComposerStats()
  const byEra = { Baroque: [], Classique: [], Romantique: [], Moderne: [] }
  for (const c of stats) {
    if (c.era && byEra[c.era]) byEra[c.era].push(c)
  }
  return byEra
}

/**
 * Retourne une suggestion aléatoire { composer, workTitle } pondérée vers les sous-représentés.
 * @param {Map<string, number>|null} exerciseCounts - Cache des comptages d'exercices par compositeur (ou null)
 * @returns {Promise<{ composer: string, workTitle: string }|null>}
 */
export async function getRandomWorkSuggestion(exerciseCounts = null) {
  await ensureLoaded()
  if (!slugToData || slugToData.size === 0) return null

  const candidates = []
  for (const [, data] of slugToData) {
    if (!data.composerName || !data.works?.length) continue
    if (data.works.length < 10) continue
    const family = (data.composerName || '').trim().split(/\s+/).pop() || data.composerName
    const slug = composerToSlug(family)
    const exCount = exerciseCounts?.get(slug) || 0
    const weight = data.works.length / (exCount + 1)
    candidates.push({ data, weight })
  }
  if (candidates.length === 0) return null

  const totalWeight = candidates.reduce((s, c) => s + c.weight, 0)
  let r = Math.random() * totalWeight
  let chosen = candidates[0]
  for (const c of candidates) {
    r -= c.weight
    if (r <= 0) { chosen = c; break }
  }

  const works = chosen.data.works
  const work = works[Math.floor(Math.random() * works.length)]
  return { composer: chosen.data.composerName, workTitle: work.workTitle }
}
